import { useIntl } from 'react-intl';
import { cn } from '@/helpers/classnames';
import {
  CLAIM_STATES,
  SCHEDULED_DESCRIPTION,
  SCHEDULED_LABEL,
} from '@/helpers/claimStates';
import { formatDate } from '@/helpers/formatting';
import {
  claimStateRevokedBecauseStudentWillInactivate,
  ticketRevocationMessages,
} from '@/helpers/ticketRevocation';
import type { CollectionTicketClaimMemberStateDTO } from '@/api/trafikportalen';

interface ClaimStateBadgesProps {
  claimStates: CollectionTicketClaimMemberStateDTO[];
  scheduledCount?: number;
  className?: string;
  inactivateAt?: string;
}

export const ClaimStateBadges = ({
  claimStates,
  scheduledCount = 0,
  className,
  inactivateAt,
}: ClaimStateBadgesProps) => {
  const { formatMessage } = useIntl();

  if (claimStates.length === 0 && scheduledCount === 0) return null;

  const scheduledLabel = formatMessage(SCHEDULED_LABEL);
  const scheduledDescription = formatMessage(SCHEDULED_DESCRIPTION);

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {Array.from({ length: scheduledCount }, (_, i) => (
        <span
          key={`scheduled-${i}`}
          className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
          title={scheduledDescription}
        >
          <span>{scheduledLabel}</span>
          <span className="sr-only">. {scheduledDescription}</span>
        </span>
      ))}
      {claimStates.map((state) => {
        const revoked =
          !!inactivateAt &&
          claimStateRevokedBecauseStudentWillInactivate(state.state, {
            active: true,
            inactivate_at: inactivateAt,
          });
        const config = CLAIM_STATES[state.state];
        const date = inactivateAt ? formatDate(inactivateAt) : '';
        const label = revoked
          ? formatMessage(ticketRevocationMessages.label)
          : formatMessage(config.label);
        const reason = revoked
          ? formatMessage(ticketRevocationMessages.reason, { date })
          : undefined;
        const detail =
          reason ??
          (state.message && state.message !== label
            ? state.message
            : undefined);
        return (
          <span
            key={`${state.collection_member_id}-${state.state}`}
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              revoked ? 'bg-yellow-100 text-yellow-800' : config.color,
            )}
            title={detail ?? label}
          >
            <span>{label}</span>
            {detail ? <span className="sr-only">. {detail}</span> : null}
          </span>
        );
      })}
    </div>
  );
};
